import { useState, useCallback, useEffect } from 'react';
import { PRIZE_POOL_ADDRESS, LILAPE_JETTON_ADDRESS, LILAPE_DECIMALS } from '../config';
import { buildJettonTransferPayload, getUserJettonWallet } from '../jettonTransfer';

const API_BASE = '/.netlify/functions/game-balance';
const DECIMALS_MULTIPLIER = BigInt(10 ** LILAPE_DECIMALS); // 10^9 for converting UI amounts to nano

/**
 * Server-side game balance hook with blockchain verification
 *
 * Security:
 * - Balance stored on server (Redis), not localStorage
 * - Deposits verified through TON blockchain
 * - Withdrawals limited to 2x deposited amount
 * - Rate limiting on all operations
 */
export const useGameBalance = (walletAddress, sendTransaction) => {
  const [gameBalance, setGameBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [depositing, setDepositing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState(null);

  // Fetch balance from server on mount and address change
  useEffect(() => {
    if (!walletAddress) {
      setGameBalance(0);
      setLoading(false);
      return;
    }

    const fetchBalance = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}?action=balance&address=${walletAddress}`);
        const data = await res.json();
        if (res.ok) {
          // Use Number() for balance - works for values up to ~9 quadrillion
          const bal = Number(data.balance) || 0;
          console.log('Fetched game balance:', data.balance, '→', bal);
          setGameBalance(bal);
        } else {
          console.error('Failed to fetch balance:', data.error);
        }
      } catch (err) {
        console.error('Balance fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [walletAddress]);

  // Deposit LILAPE to game (with blockchain verification)
  const deposit = useCallback(async (amountLilape) => {
    if (!walletAddress || !sendTransaction) {
      setError('Wallet not connected');
      return false;
    }

    setDepositing(true);
    setError(null);

    try {
      // Get user's jetton wallet
      const userJettonWallet = await getUserJettonWallet(walletAddress, LILAPE_JETTON_ADDRESS);
      if (!userJettonWallet) {
        throw new Error('LILAPE wallet not found');
      }

      // Build transfer payload
      // amountLilape is in UI units (e.g. 5_000_000_000 for "5B")
      // Need to multiply by 10^9 decimals to get actual nano units for blockchain
      const amountNano = BigInt(amountLilape) * DECIMALS_MULTIPLIER;
      const payload = await buildJettonTransferPayload(amountNano, PRIZE_POOL_ADDRESS);

      // Send transaction - this returns after user confirms in wallet
      const result = await sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [{
          address: userJettonWallet,
          amount: String(50000000), // 0.05 TON for gas
          payload
        }],
      });

      // Extract transaction hash from result
      // TonConnect returns boc (bag of cells) which we can use as tx identifier
      const txHash = result?.boc || `deposit_${Date.now()}_${walletAddress}`;

      // Wait for transaction to appear on blockchain (usually 5-10 seconds)
      await new Promise(resolve => setTimeout(resolve, 8000));

      // Verify deposit on server
      const verifyRes = await fetch(`${API_BASE}?action=deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: walletAddress,
          txHash,
          amount: amountLilape
        }),
      });

      const verifyData = await verifyRes.json();

      if (!verifyRes.ok) {
        throw new Error(verifyData.error || 'Deposit verification failed');
      }

      // Update local state with server balance
      setGameBalance(Number(verifyData.balance) || 0);

      return true;
    } catch (err) {
      if (!err.message?.includes('rejected') && !err.message?.includes('cancelled')) {
        setError(err.message);
      }
      return false;
    } finally {
      setDepositing(false);
    }
  }, [walletAddress, sendTransaction]);

  // Withdraw LILAPE from game to wallet (server-verified)
  const withdraw = useCallback(async (amountLilape) => {
    if (!walletAddress) {
      setError('Wallet not connected');
      return false;
    }

    if (amountLilape > gameBalance) {
      setError('Insufficient game balance');
      return false;
    }

    setWithdrawing(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}?action=withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: walletAddress,
          amount: amountLilape
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Withdrawal failed');
      }

      // Update local state with server balance
      setGameBalance(Number(data.balance) || 0);

      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setWithdrawing(false);
    }
  }, [walletAddress, gameBalance]);

  // Place bet (server-side deduction)
  const useSpin = useCallback(async (betAmount, gameId = 'slots') => {
    if (betAmount > gameBalance) {
      return false;
    }

    try {
      const res = await fetch(`${API_BASE}?action=bet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: walletAddress,
          amount: betAmount,
          gameId
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error('Bet failed:', data.error);
        return false;
      }

      setGameBalance(Number(data.balance) || 0);
      return true;
    } catch (err) {
      console.error('Bet error:', err);
      return false;
    }
  }, [walletAddress, gameBalance]);

  // Add winnings (server-side credit)
  const addWinnings = useCallback(async (amount, gameId = 'slots', betAmount = 0) => {
    if (amount <= 0) return;

    try {
      const res = await fetch(`${API_BASE}?action=win`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: walletAddress,
          amount,
          gameId,
          betAmount
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error('Win credit failed:', data.error);
        return;
      }

      setGameBalance(Number(data.balance) || 0);
    } catch (err) {
      console.error('Win credit error:', err);
    }
  }, [walletAddress]);

  // Refresh balance from server
  const refetch = useCallback(async () => {
    if (!walletAddress) return;

    try {
      const res = await fetch(`${API_BASE}?action=balance&address=${walletAddress}`);
      const data = await res.json();
      if (res.ok) {
        setGameBalance(Number(data.balance) || 0);
      }
    } catch (err) {
      console.error('Refetch error:', err);
    }
  }, [walletAddress]);

  return {
    gameBalance,
    loading,
    depositing,
    withdrawing,
    error,
    deposit,
    withdraw,
    useSpin,
    addWinnings,
    refetch,
    canSpin: (betAmount) => gameBalance >= betAmount,
  };
};
